import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Promotion Editor Component
 *
 * Create and edit product promotions with dynamic form (M3 Wave 6)
 *
 * @package WooAIChatbot
 */
import { useState, useEffect } from 'react';
import { ArrowLeft, Save, X, Plus, Trash2 } from 'lucide-react';
import { useAsync } from '../../../utils/hooks/useAsync';
import { promotionsApi } from '../../../utils/api';
import { useTranslation } from '../../../hooks/useTranslation';
import { ProductSelector } from '../../components/ProductSelector';
// Inline styles
const styles = {
    page: {
        padding: '32px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    backButton: {
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    },
    headerContent: {
        flex: 1,
    },
    title: {
        fontSize: '24px',
        fontWeight: 500,
        color: '#111827',
        margin: 0,
    },
    subtitle: {
        fontSize: '14px',
        color: '#6b7280',
        margin: 0,
    },
    actions: {
        display: 'flex',
        gap: '12px',
    },
    cancelButton: {
        height: '40px',
        padding: '0 16px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        backgroundColor: '#ffffff',
        color: '#374151',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    saveButton: {
        height: '40px',
        padding: '0 16px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    card: {
        backgroundColor: '#ffffff',
        borderRadius: '14px',
        border: '1px solid #e5e7eb',
        padding: '24px',
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginBottom: '20px',
    },
    label: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#374151',
    },
    input: {
        height: '40px',
        padding: '0 12px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        fontSize: '14px',
        color: '#111827',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
    },
    select: {
        height: '40px',
        padding: '0 12px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        fontSize: '14px',
        color: '#111827',
        backgroundColor: '#ffffff',
        cursor: 'pointer',
        boxSizing: 'border-box',
    },
    rangeInput: {
        width: '100%',
        height: '8px',
        borderRadius: '4px',
        appearance: 'none',
        backgroundColor: '#e5e7eb',
        outline: 'none',
    },
    rangeValue: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#6366f1',
        textAlign: 'center',
    },
    switchContainer: {
        position: 'relative',
        width: '44px',
        height: '24px',
    },
    switch: {
        width: '44px',
        height: '24px',
        borderRadius: '9999px',
        border: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    },
    switchOn: {
        backgroundColor: '#6366f1',
    },
    switchOff: {
        backgroundColor: '#d1d5db',
    },
    switchThumb: {
        position: 'absolute',
        top: '2px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: '#ffffff',
        transition: 'transform 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        pointerEvents: 'none',
    },
    switchThumbOn: {
        transform: 'translateX(22px)',
    },
    switchThumbOff: {
        transform: 'translateX(2px)',
    },
    switchRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 0',
    },
    switchLabel: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#374151',
    },
    helpText: {
        fontSize: '12px',
        color: '#6b7280',
        marginTop: '4px',
    },
    rulesSection: {
        marginTop: '24px',
        padding: '20px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
    },
    rulesHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
    },
    rulesTitle: {
        fontSize: '16px',
        fontWeight: 500,
        color: '#111827',
        margin: 0,
    },
    addRuleButton: {
        height: '32px',
        padding: '0 12px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        backgroundColor: '#ffffff',
        color: '#374151',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    ruleCard: {
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        padding: '16px',
        marginBottom: '12px',
    },
    ruleHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
    },
    ruleLabel: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#374151',
    },
    deleteRuleButton: {
        width: '28px',
        height: '28px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    },
    logicToggle: {
        display: 'flex',
        gap: '8px',
        marginTop: '12px',
    },
    logicButton: {
        height: '32px',
        padding: '0 16px',
        borderRadius: '6px',
        border: '1px solid #d1d5db',
        backgroundColor: '#ffffff',
        color: '#4b5563',
        fontSize: '12px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    logicButtonActive: {
        height: '32px',
        padding: '0 16px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        fontSize: '12px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    productSelector: {
        marginTop: '16px',
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
    },
};
// Toggle switch component
function ToggleSwitch({ checked, onChange }) {
    return (_jsxs("div", { style: styles.switchContainer, onClick: onChange, children: [_jsx("div", { style: { ...styles.switch, ...(checked ? styles.switchOn : styles.switchOff) } }), _jsx("div", { style: { ...styles.switchThumb, ...(checked ? styles.switchThumbOn : styles.switchThumbOff) } })] }));
}
export function PromotionEditor({ promotionId, onBack }) {
    const { t } = useTranslation();
    const isEditMode = !!promotionId;
    // Form state - default active: false to allow creating draft promotions
    const [formData, setFormData] = useState({
        name: '',
        type: 'manual',
        active: false,
        product_ids: [],
        rules: [],
        rule_logic: 'AND',
        boost_multiplier: 2.0,
        valid_from: null,
        valid_to: null,
    });
    // Load promotion data if editing
    const { data: promotion, loading } = useAsync(() => promotionId ? promotionsApi.getOne(promotionId) : Promise.resolve(null), [promotionId]);
    useEffect(() => {
        if (promotion) {
            setFormData({
                name: promotion.name,
                type: promotion.type,
                active: promotion.active,
                product_ids: promotion.product_ids,
                rules: promotion.rules,
                rule_logic: promotion.rule_logic,
                boost_multiplier: promotion.boost_multiplier,
                valid_from: promotion.valid_from,
                valid_to: promotion.valid_to,
            });
        }
    }, [promotion]);
    // Handle form field changes
    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };
    // Handle type change - reset rules/products
    const handleTypeChange = (type) => {
        setFormData(prev => ({
            ...prev,
            type,
            product_ids: type === 'manual' ? prev.product_ids : [],
            rules: type === 'automatic' ? prev.rules : [],
        }));
    };
    // Add new rule
    const handleAddRule = () => {
        const newRule = {
            type: 'on_sale',
        };
        setFormData(prev => ({
            ...prev,
            rules: [...prev.rules, newRule],
        }));
    };
    // Update rule
    const handleUpdateRule = (index, updates) => {
        setFormData(prev => ({
            ...prev,
            rules: prev.rules.map((rule, i) => i === index ? { ...rule, ...updates } : rule),
        }));
    };
    // Delete rule
    const handleDeleteRule = (index) => {
        setFormData(prev => ({
            ...prev,
            rules: prev.rules.filter((_, i) => i !== index),
        }));
    };
    // Save promotion
    const handleSave = async () => {
        // Frontend validation
        if (!formData.name.trim()) {
            alert(t('pleaseEnterPromotionName'));
            return;
        }
        // Validate active manual promotions require products
        if (formData.active && formData.type === 'manual' && formData.product_ids.length === 0) {
            alert(t('activeManualPromotionsRequireProducts'));
            return;
        }
        // Validate active automatic promotions require rules
        if (formData.active && formData.type === 'automatic' && formData.rules.length === 0) {
            alert(t('activeAutomaticPromotionsRequireRules'));
            return;
        }
        try {
            if (isEditMode) {
                await promotionsApi.update(promotionId, formData);
            }
            else {
                await promotionsApi.create(formData);
            }
            onBack();
        }
        catch (err) {
            console.error('Failed to save promotion:', err);
            const errorMessage = err?.message || err?.data?.message || t('unknownError');
            alert(`${t('failedToSavePromotion')}: ${errorMessage}`);
        }
    };
    if (loading) {
        return (_jsx("div", { style: styles.page, children: _jsx("div", { style: { textAlign: 'center', padding: '48px 0' }, children: t('loadingPromotion') }) }));
    }
    return (_jsxs("div", { style: styles.page, children: [_jsxs("div", { style: styles.header, children: [_jsx("button", { style: styles.backButton, onClick: onBack, children: _jsx(ArrowLeft, { style: { width: '20px', height: '20px', color: '#374151' } }) }), _jsxs("div", { style: styles.headerContent, children: [_jsx("h1", { style: styles.title, children: isEditMode ? t('editPromotion') : t('newPromotion') }), _jsx("p", { style: styles.subtitle, children: formData.type === 'manual' ? t('manuallySelectProductsToPromote') : t('setupAutomaticRulesForProductSelection') })] }), _jsxs("div", { style: styles.actions, children: [_jsxs("button", { style: styles.cancelButton, onClick: onBack, children: [_jsx(X, { style: { width: '16px', height: '16px' } }), t('cancel')] }), _jsxs("button", { style: styles.saveButton, onClick: handleSave, children: [_jsx(Save, { style: { width: '16px', height: '16px' } }), t('save')] })] })] }), _jsxs("div", { style: styles.card, children: [_jsxs("div", { style: styles.formGroup, children: [_jsx("label", { style: styles.label, children: t('promotionName') }), _jsx("input", { type: "text", style: styles.input, value: formData.name, onChange: (e) => handleChange('name', e.target.value), placeholder: t('promotionNamePlaceholder') })] }), _jsxs("div", { style: styles.formGroup, children: [_jsx("label", { style: styles.label, children: t('promotionType') }), _jsxs("select", { style: styles.select, value: formData.type, onChange: (e) => handleTypeChange(e.target.value), children: [_jsx("option", { value: "manual", children: t('manualSelectSpecificProducts') }), _jsx("option", { value: "automatic", children: t('automaticUseRulesToSelectProducts') })] }), _jsx("p", { style: styles.helpText, children: formData.type === 'manual'
                                    ? t('manuallyChooseWhichProductsToPromote')
                                    : t('automaticallySelectProductsBasedOnRules') })] }), _jsxs("div", { style: styles.switchRow, children: [_jsxs("div", { children: [_jsx("div", { style: styles.switchLabel, children: t('active') }), _jsx("p", { style: styles.helpText, children: t('enableOrDisableThisPromotion') })] }), _jsx(ToggleSwitch, { checked: formData.active, onChange: () => handleChange('active', !formData.active) })] }), _jsxs("div", { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '20px' }, children: [_jsxs("div", { style: styles.formGroup, children: [_jsx("label", { style: styles.label, children: t('validFrom') }), _jsx("input", { type: "date", style: styles.input, value: formData.valid_from || '', onChange: (e) => handleChange('valid_from', e.target.value || null) })] }), _jsxs("div", { style: styles.formGroup, children: [_jsx("label", { style: styles.label, children: t('validTo') }), _jsx("input", { type: "date", style: styles.input, value: formData.valid_to || '', onChange: (e) => handleChange('valid_to', e.target.value || null) })] })] }), _jsxs("div", { style: styles.formGroup, children: [_jsx("label", { style: styles.label, children: t('boostMultiplier') }), _jsxs("div", { style: styles.rangeValue, children: [formData.boost_multiplier.toFixed(1), "x"] }), _jsx("input", { type: "range", min: "1.0", max: "5.0", step: "0.1", style: styles.rangeInput, value: formData.boost_multiplier, onChange: (e) => handleChange('boost_multiplier', parseFloat(e.target.value)) }), _jsx("p", { style: styles.helpText, children: t('boostMultiplierDescription') })] }), formData.type === 'manual' && (_jsxs("div", { style: styles.productSelector, children: [_jsx("div", { style: styles.label, children: t('selectProducts') }), _jsx("p", { style: styles.helpText, children: t('searchAndSelectProductsToPromote') }), _jsx("div", { style: { marginTop: '12px' }, children: _jsx(ProductSelector, { selectedIds: formData.product_ids, onChange: (ids) => handleChange('product_ids', ids), placeholder: t('searchProducts') }) })] })), formData.type === 'automatic' && (_jsxs("div", { style: styles.rulesSection, children: [_jsxs("div", { style: styles.rulesHeader, children: [_jsx("h3", { style: styles.rulesTitle, children: t('automaticRules') }), _jsxs("button", { style: styles.addRuleButton, onClick: handleAddRule, children: [_jsx(Plus, { style: { width: '14px', height: '14px' } }), t('addRule')] })] }), formData.rules.length === 0 ? (_jsx("div", { style: { textAlign: 'center', padding: '24px', color: '#6b7280' }, children: t('noRulesYet') })) : (_jsxs(_Fragment, { children: [formData.rules.map((rule, index) => (_jsxs("div", { style: styles.ruleCard, children: [_jsxs("div", { style: styles.ruleHeader, children: [_jsxs("label", { style: styles.ruleLabel, children: [t('rule'), " ", index + 1] }), _jsx("button", { style: styles.deleteRuleButton, onClick: () => handleDeleteRule(index), "aria-label": t('deleteRule'), children: _jsx(Trash2, { style: { width: '14px', height: '14px', color: '#dc2626' } }) })] }), _jsxs("div", { style: styles.formGroup, children: [_jsx("label", { style: styles.label, children: t('ruleType') }), _jsxs("select", { style: styles.select, value: rule.type, onChange: (e) => handleUpdateRule(index, { type: e.target.value }), children: [_jsx("option", { value: "on_sale", children: t('productsOnSale') }), _jsx("option", { value: "category", children: t('productsInCategory') }), _jsx("option", { value: "best_sellers", children: t('bestSellers') })] })] }), rule.type === 'category' && (_jsxs("div", { style: styles.formGroup, children: [_jsx("label", { style: styles.label, children: t('categories') }), _jsx("div", { style: { padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '6px' }, children: t('categoryMultiSelectWillBeHere') })] })), rule.type === 'best_sellers' && (_jsxs("div", { style: styles.formGroup, children: [_jsx("label", { style: styles.label, children: t('limitNumberOfTopSellers') }), _jsx("input", { type: "number", min: "1", max: "50", style: styles.input, value: rule.limit || 10, onChange: (e) => handleUpdateRule(index, { limit: parseInt(e.target.value) }) })] }))] }, index))), formData.rules.length > 1 && (_jsxs("div", { style: { marginTop: '16px' }, children: [_jsx("label", { style: styles.label, children: t('ruleLogic') }), _jsxs("div", { style: styles.logicToggle, children: [_jsx("button", { style: formData.rule_logic === 'AND' ? styles.logicButtonActive : styles.logicButton, onClick: () => handleChange('rule_logic', 'AND'), children: t('andAllRulesMustMatch') }), _jsx("button", { style: formData.rule_logic === 'OR' ? styles.logicButtonActive : styles.logicButton, onClick: () => handleChange('rule_logic', 'OR'), children: t('orAnyRuleCanMatch') })] })] }))] }))] }))] })] }));
}
export default PromotionEditor;
